// Settings UI for the distribution model — sits beneath the API-tokens
// block on the Settings page. Three subsections:
//
//   1. Distribution tier %s per segment (PATCH /api/distribution-config/:id)
//   2. Operator splits per segment      (PUT  /api/segment-operators/:id)
//   3. CapEx pool balances + ledger     (GET  /api/capex/pools(/:id/ledger))
//
// All owner-only. Endpoints already exist; this is purely the UI.

import { useEffect, useState } from 'react';
import { api, fmtMoney, fmtDate } from '../lib/api.js';

export default function DistributionSettings() {
  return (
    <>
      <DistributionConfigSection />
      <OperatorsSection />
      <CapExPoolsSection />
    </>
  );
}

// ─── 1. Tier-% editor ─────────────────────────────────────────────────────

function DistributionConfigSection() {
  const [configs, setConfigs] = useState(null);
  const [err, setErr] = useState('');

  function load() {
    api.get('/api/distribution-config').then(d => setConfigs(d.configs || [])).catch(e => setErr(e.message));
  }
  useEffect(load, []);

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">Distribution tier %s</h2>
        <p className="text-sm text-ink-700/70">
          Two tiers per segment. Tier 1 is active while CapEx is being paid down (operator + Jeff + CapEx must sum to 100).
          Tier 2 kicks in once the CapEx pool hits $0 (operator + Jeff must sum to 100).
        </p>
      </div>
      {err && <p className="text-rust-700 text-sm">{err}</p>}
      {!configs ? (
        <p className="text-ink-700/60 italic">Loading…</p>
      ) : (
        <div className="space-y-3">
          {configs.map(c => <TierConfigCard key={c.segment_id} cfg={c} onSaved={load} />)}
        </div>
      )}
    </section>
  );
}

function TierConfigCard({ cfg, onSaved }) {
  const [t1Op,    setT1Op]    = useState(cfg.tier1_operator_pct);
  const [t1Jeff,  setT1Jeff]  = useState(cfg.tier1_jeff_pct);
  const [t1Cap,   setT1Cap]   = useState(cfg.tier1_capex_pct);
  const [t2Op,    setT2Op]    = useState(cfg.tier2_operator_pct);
  const [t2Jeff,  setT2Jeff]  = useState(cfg.tier2_jeff_pct);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const t1Sum = t1Op + t1Jeff + t1Cap;
  const t2Sum = t2Op + t2Jeff;

  async function save() {
    if (t1Sum !== 100) { setErr(`Tier 1 must sum to 100 (got ${t1Sum})`); return; }
    if (t2Sum !== 100) { setErr(`Tier 2 must sum to 100 (got ${t2Sum})`); return; }
    setBusy(true); setErr('');
    try {
      await api.patch(`/api/distribution-config/${cfg.segment_id}`, {
        tier1_operator_pct: t1Op, tier1_jeff_pct: t1Jeff, tier1_capex_pct: t1Cap,
        tier2_operator_pct: t2Op, tier2_jeff_pct: t2Jeff,
      });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-olive-100 rounded p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="font-medium text-ink-900 capitalize">{cfg.segment_name}</div>
        <div className="text-xs text-ink-700/60">Jeff = {cfg.jeff_name}</div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <Pct label="T1 Operator" value={t1Op}   onChange={setT1Op} />
        <Pct label="T1 Jeff"     value={t1Jeff} onChange={setT1Jeff} />
        <Pct label="T1 CapEx"    value={t1Cap}  onChange={setT1Cap} />
      </div>
      <div className={`text-xs ${t1Sum !== 100 ? 'text-rust-700' : 'text-ink-700/60'}`}>Tier 1 sum: {t1Sum} / 100</div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <Pct label="T2 Operator" value={t2Op}   onChange={setT2Op} />
        <Pct label="T2 Jeff"     value={t2Jeff} onChange={setT2Jeff} />
      </div>
      <div className={`text-xs ${t2Sum !== 100 ? 'text-rust-700' : 'text-ink-700/60'}`}>Tier 2 sum: {t2Sum} / 100</div>

      {err && <p className="text-xs text-rust-700">{err}</p>}
      <div className="flex justify-end">
        <button className="btn-primary text-xs py-1 px-3" disabled={busy || t1Sum !== 100 || t2Sum !== 100} onClick={save}>
          {busy ? 'Saving…' : 'Save defaults'}
        </button>
      </div>
    </div>
  );
}

// ─── 2. Operator splits per segment ───────────────────────────────────────

function OperatorsSection() {
  const [grouped, setGrouped] = useState(null);
  const [users,   setUsers]   = useState([]);
  const [err,     setErr]     = useState('');

  function load() {
    Promise.all([api.get('/api/segment-operators'), api.get('/api/users')])
      .then(([ops, us]) => {
        const g = {};
        for (const r of (ops.operators || [])) {
          if (!g[r.segment_id]) g[r.segment_id] = { name: r.segment_name, rows: [] };
          g[r.segment_id].rows.push({ user_id: r.user_id, user_name: r.user_name, share_pct: r.share_pct });
        }
        // Ensure all three livestock segments show up even if empty
        for (const seg of ['meat_chickens', 'egg_chickens', 'cattle']) {
          if (!g[seg]) g[seg] = { name: prettySeg(seg), rows: [] };
        }
        setGrouped(g);
        setUsers(us.users || []);
      })
      .catch(e => setErr(e.message));
  }
  useEffect(load, []);

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">Operator splits</h2>
        <p className="text-sm text-ink-700/70">
          Each segment's operator pool is divided among these people by share %. Shares must sum to 100.
          Adding a new operator slices the pool further — it does NOT reduce Jeff's share.
        </p>
      </div>
      {err && <p className="text-rust-700 text-sm">{err}</p>}
      {!grouped ? (
        <p className="text-ink-700/60 italic">Loading…</p>
      ) : (
        Object.entries(grouped).map(([segId, g]) => (
          <OperatorCard key={segId} segmentId={segId} segmentName={g.name} rows={g.rows} users={users} onSaved={load} />
        ))
      )}
    </section>
  );
}

function OperatorCard({ segmentId, segmentName, rows, users, onSaved }) {
  const [splits, setSplits] = useState(rows.map(r => ({ user_id: r.user_id, share_pct: r.share_pct })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const sum = splits.reduce((s, r) => s + Number(r.share_pct || 0), 0);

  function addRow() {
    const taken = new Set(splits.map(s => s.user_id));
    const next = users.find(u => !taken.has(u.id));
    if (!next) { setErr('All users already assigned'); return; }
    setSplits([...splits, { user_id: next.id, share_pct: 0 }]);
  }
  function removeRow(i) { setSplits(arr => arr.filter((_, j) => j !== i)); }
  function update(i, patch) { setSplits(arr => arr.map((r, j) => j === i ? { ...r, ...patch } : r)); }

  async function save() {
    if (sum !== 100 && sum !== 0) { setErr(`Shares must sum to 100 (got ${sum})`); return; }
    setBusy(true); setErr('');
    try {
      await api.put(`/api/segment-operators/${segmentId}`, { splits: splits.filter(s => s.share_pct > 0) });
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-olive-100 rounded p-3 space-y-2">
      <div className="font-medium text-ink-900">{segmentName}</div>
      {splits.length === 0 ? (
        <p className="text-xs text-ink-700/60 italic">No operators configured for this segment.</p>
      ) : splits.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <select
            className="input flex-1"
            value={s.user_id}
            onChange={e => update(i, { user_id: Number(e.target.value) })}
          >
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <Pct value={s.share_pct} onChange={v => update(i, { share_pct: v })} />
          <button className="text-xs text-rust-700 hover:underline px-1" onClick={() => removeRow(i)}>×</button>
        </div>
      ))}
      <div className={`text-xs ${sum !== 100 && sum !== 0 ? 'text-rust-700' : 'text-ink-700/60'}`}>Sum: {sum} / 100</div>
      {err && <p className="text-xs text-rust-700">{err}</p>}
      <div className="flex gap-2 justify-end">
        <button className="btn-ghost text-xs py-1 px-2" onClick={addRow}>+ Add operator</button>
        <button className="btn-primary text-xs py-1 px-3" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save splits'}
        </button>
      </div>
    </div>
  );
}

// ─── 3. CapEx pools + ledger ──────────────────────────────────────────────

function CapExPoolsSection() {
  const [pools, setPools] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [ledgers, setLedgers] = useState({});
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/capex/pools').then(d => setPools(d.pools || [])).catch(e => setErr(e.message));
  }, []);

  async function toggle(poolId) {
    if (expanded === poolId) { setExpanded(null); return; }
    setExpanded(poolId);
    if (!ledgers[poolId]) {
      try {
        const d = await api.get(`/api/capex/pools/${poolId}/ledger`);
        setLedgers(L => ({ ...L, [poolId]: d.ledger || [] }));
      } catch (e) { setErr(e.message); }
    }
  }

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">CapEx pools</h2>
        <p className="text-sm text-ink-700/70">
          Each segment's CapEx debt is tracked here. Positive balance = debt (Tier 1 active). $0 = paid off (Tier 2).
          New CapEx flows in via receipt lines flagged as Capital expense.
        </p>
      </div>
      {err && <p className="text-rust-700 text-sm">{err}</p>}
      {!pools ? (
        <p className="text-ink-700/60 italic">Loading…</p>
      ) : (
        pools.map(p => (
          <div key={p.id} className="border border-olive-100 rounded p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-medium">{p.name}</div>
                {p.notes && <div className="text-xs text-ink-700/60">{p.notes}</div>}
              </div>
              <div className="text-right">
                <div className={`text-xl font-semibold ${p.balance_cents > 0 ? 'text-rust-700' : 'text-olive-700'}`}>
                  {fmtMoney(p.balance_cents)}
                </div>
                <button className="text-xs text-olive-700 hover:underline" onClick={() => toggle(p.id)}>
                  {expanded === p.id ? 'Hide ledger' : 'Show ledger'}
                </button>
              </div>
            </div>
            {expanded === p.id && (
              <table className="w-full text-xs mt-2">
                <thead>
                  <tr className="text-ink-700/60 border-b border-olive-100">
                    <th className="text-left py-1 px-2">Date</th>
                    <th className="text-left py-1 px-2">Description</th>
                    <th className="text-left py-1 px-2">Kind</th>
                    <th className="text-left py-1 px-2">Vendor</th>
                    <th className="text-right py-1 px-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(ledgers[p.id] || []).map(l => (
                    <tr key={l.id} className="border-b border-olive-100/40">
                      <td className="py-1 px-2">{fmtDate(l.date)}</td>
                      <td className="py-1 px-2">{l.description}</td>
                      <td className="py-1 px-2">
                        <span className="pill bg-cream-100 text-ink-700">{l.kind}</span>
                      </td>
                      <td className="py-1 px-2 text-ink-700/70">{l.expense_vendor || '—'}</td>
                      <td className={`py-1 px-2 text-right ${l.amount_cents > 0 ? 'text-rust-700' : 'text-olive-700'}`}>
                        {l.amount_cents > 0 ? '+' : ''}{fmtMoney(l.amount_cents)}
                      </td>
                    </tr>
                  ))}
                  {(ledgers[p.id] || []).length === 0 && (
                    <tr><td colSpan={5} className="py-2 text-center text-ink-700/60 italic">No ledger entries.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        ))
      )}
    </section>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function Pct({ label, value, onChange }) {
  return (
    <div>
      {label && <label className="block text-xs text-ink-700/60">{label}</label>}
      <input
        type="number"
        min="0"
        max="100"
        className="input py-1 w-20"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function prettySeg(id) {
  return { meat_chickens: 'Meat Chickens', egg_chickens: 'Egg Chickens', cattle: 'Cattle' }[id] || id;
}
